import Head from 'next/head';
import '@ozoneuniverse/canvas-components/dist/styles/build.css';
import React, { useState, createContext, useEffect, useCallback } from 'react';
import { SITE_PREVIEW } from '@src/theme/branding/site-preview';
import { BRANDING, getBranding } from 'util/constants';
import Script from 'next/script';
import TopLevelLoader from '@src/components/top-level-loader';
import { SessionProvider } from 'next-auth/react';
import { ApolloProvider } from '@apollo/client';
import { useApollo } from '../src/apolloClient';
import { store } from '../src/app/store';
import type { AppProps } from 'next/app';
import { IS_PROD, isDev, isStaging } from 'util/constants';
import type { Session } from 'next-auth';
import { Provider } from 'react-redux';
import useIsLoggedIn from 'auth/hooks/useIsLoggedIn';
import dynamic from 'next/dynamic';
import Bugsnag from '@bugsnag/js';
import { ConfigProvider, theme } from 'antd';
import '@ozoneuniverse/mfe-core/dist/components/css3Dfix.css';
import { GeistSans } from 'geist/font/sans';
import { useRouter } from 'next/router';
import exposeBlueprintsDependencies from '@/components/editor/blueprints/exposeBlueprintsDependencies';
import './index.css';

exposeBlueprintsDependencies();

const Notification = dynamic(
  () => import('@src/components/ui-kit/notification'),
  { ssr: false }
);

const Login = dynamic(() => import('../auth/ui/LoginPage'), { ssr: false });

const BrandingSwitcher = dynamic(
  () => import('@src/components/branding-switcher/BrandingSwitcher'),
  { ssr: false }
);

export const BrandingContext = createContext('');
export const ThemeContext = createContext({
  isDark: false,
  toggleTheme: () => {},
});

const ErrorBoundary = Bugsnag?.getPlugin('react')?.createErrorBoundary(
  React
) as any;

const ErrorView = () => (
  <div>
    <p>Error occurred please contact Ozonemetaverse support.</p>
  </div>
);

export default function MyApp({
  Component,
  pageProps: { session, ...pageProps },
}: AppProps<{ session: Session }>) {
  const apolloClient = useApollo(pageProps);
  const [branding, setBranding] = useState(BRANDING);
  const [preview, setPreview] = useState(
    SITE_PREVIEW[BRANDING] || SITE_PREVIEW.DEFAULT
  );
  const [isDark, setIsDark] = useState(false);
  const { isLoggedIn } = useIsLoggedIn();
  const [inEditor, setInEditor] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const isCurrentlyInEditor =
      router.pathname === '/user/studio/scene/[id]' ||
      router.pathname === '/new-template';

    setInEditor(isCurrentlyInEditor);

    if (isCurrentlyInEditor) {
      const savedTheme = localStorage.getItem('isDarkTheme');
      setIsDark(savedTheme ? JSON.parse(savedTheme) : false);
    } else {
      setIsDark(false);
    }
  }, [router.pathname]);

  useEffect(() => {
    const handleStorageChange = () => {
      const newBranding = getBranding();
      setBranding(newBranding);
      setPreview(SITE_PREVIEW[newBranding] || SITE_PREVIEW.DEFAULT);
    };

    handleStorageChange();
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const toggleTheme = useCallback(() => {
    const newThemeValue = !isDark;
    setIsDark(newThemeValue);
    localStorage.setItem('isDarkTheme', JSON.stringify(newThemeValue));
  }, [isDark]);

  return (
    <>
      <Head>
        <meta charSet='utf-8' />
        <link rel='icon' href={preview?.favicon} />
        <meta name='viewport' content='width=device-width, initial-scale=1' />
        <meta name='theme-color' content='#000000' />
        <link rel='apple-touch-icon' href='%PUBLIC_URL%/logo192.png' />
        <title>{preview?.pageTitle}</title>
        <meta property='og:title' content={preview?.ogTitle} />
        <meta property='twitter:title' content={preview?.ogTitle} />
        <meta name='og:description' content={preview?.ogDesc} />
        <meta name='twitter:description' content={preview?.ogDesc} />
        <meta property='og:image' content={preview?.image} />
        <meta property='twitter:image' content={preview?.image} />
        <meta property='og:image:width' content='446'></meta>
        <meta property='og:image:height' content='230'></meta>
        <meta property='twitter:card' content='summary_large_image'></meta>
        <meta property='robots' content='all'></meta>
        <style global>
          {`
            :root {
              --border-color: ${
                isDark ? 'rgb(38, 38, 38)' : 'rgba(5, 5, 5, 0.06)'
              };
              --background-color: ${isDark ? '#E02189' : '#FBDEEE'};
            }

            * {
              font-family: ${GeistSans.style.fontFamily};
            }
            html {
              background-color: ${isDark ? '#141414' : '#ffffff'};
            }

            body .layout {
              background-color: ${isDark ? '#141414' : '#ffffff'};
            }

            body {
              font-family: ${GeistSans.style.fontFamily};
              background-color: ${isDark ? '#141414' : '#ffffff'};
              color: ${isDark ? '#ffffff' : '#000000'};
            }

            .layout .header {
              background-color: ${
                isDark ? '#141414' : 'rgba(255, 255, 255, 0.954)'
              };
              border-bottom: 0.3px solid ${isDark ? '#4c4c4c9a' : '#7b7b7b9a'};
            }

            .ant-menu .ant-menu-item-selected {
              background-color: ${isDark ? '#262626' : '#ffe6ee'};
            }

            .container-sidedrawer {
              background: ${isDark ? '#1F1F1F' : '#fff'};    
            }
            
            .composer-root {
              background-color: ${isDark ? '#141414' : '#fff'}; 
            }
    `}
        </style>
      </Head>
      <Script
        src='https://www.googletagmanager.com/gtag/js?id=G-XH33563KR9'
        strategy='afterInteractive'
      />
      <Script id='google-analytics' strategy='afterInteractive'>
        {`
        window.dataLayer = window.dataLayer || [];
        function gtag(){window.dataLayer.push(arguments);}
        gtag('js', new Date());

        gtag('config', 'G-XH33563KR9');
      `}
      </Script>
      <style jsx global>{`
        html {
          font-family: ${GeistSans.style.fontFamily};
        }
      `}</style>
      {IS_PROD ? (
        <ConfigProvider
          theme={{
            algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
            token: {
              colorPrimary: '#e20073',
              borderRadius: 12,
              fontSize: 14,
              colorHighlight: '#4a4ae5',
            },
            components: {
              Alert: {
                colorBorder: '#a0a0a0',
                colorBgBase: '#b3b3b3',
                colorIcon: '#4a4ae5',
              },
              Radio: {
                colorPrimary: '#000',
                colorPrimaryHover: '#663da9',
              },
              Switch: {
                colorPrimary: '#87d068',
              },
              Tabs: {
                itemActiveColor: 'rgba(92, 54, 154, 1)',
                itemSelectedColor: 'rgba(92, 54, 154, 1)',
                inkBarColor: 'rgba(92, 54, 154, 1)',
              },
            },
          }}
        >
          <ThemeContext.Provider value={{ isDark, toggleTheme }}>
            <BrandingContext.Provider value={branding}>
              <ApolloProvider client={apolloClient}>
                <SessionProvider session={session}>
                  <Provider store={store}>
                    <TopLevelLoader>
                      {isLoggedIn ? (
                        <Component {...pageProps} suppressHydrationWarning />
                      ) : (
                        <Login />
                      )}
                      <Notification />
                      {(isDev || isStaging) && inEditor && <BrandingSwitcher />}
                    </TopLevelLoader>
                  </Provider>
                </SessionProvider>
              </ApolloProvider>
            </BrandingContext.Provider>
          </ThemeContext.Provider>
        </ConfigProvider>
      ) : (
        <ConfigProvider
          theme={{
            algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
            token: {
              colorPrimary: '#e20073',
              borderRadius: 12,
              fontSize: 14,
              colorHighlight: '#4a4ae5',
            },
            components: {
              Alert: {
                colorBorder: '#a0a0a0',
                colorBgBase: '#b3b3b3',
                colorIcon: '#4a4ae5',
              },
              Radio: {
                colorPrimary: '#000',
                colorPrimaryHover: '#663da9',
              },
              Switch: {
                colorPrimary: '#87d068',
              },
              Tabs: {
                itemActiveColor: 'rgba(92, 54, 154, 1)',
                itemSelectedColor: 'rgba(92, 54, 154, 1)',
                inkBarColor: 'rgba(92, 54, 154, 1)',
              },
            },
          }}
        >
          <ThemeContext.Provider value={{ isDark, toggleTheme }}>
            <BrandingContext.Provider value={branding}>
              <ApolloProvider client={apolloClient}>
                <SessionProvider session={session}>
                  <Provider store={store}>
                    <TopLevelLoader>
                      {isLoggedIn ? (
                        <Component {...pageProps} suppressHydrationWarning />
                      ) : (
                        <Login />
                      )}
                      <Notification />
                      {(isDev || isStaging) && inEditor && <BrandingSwitcher />}
                    </TopLevelLoader>
                  </Provider>
                </SessionProvider>
              </ApolloProvider>
            </BrandingContext.Provider>
          </ThemeContext.Provider>
        </ConfigProvider>
      )}
    </>
  );
}
